import type {JsxVNodeChildren} from 'snabbdom-jsx-lite';
import type {VNode} from 'snabbdom';
import {clsx} from 'clsx';

import {StrictJsxProps} from 'common/util/jsx';
import {react2panel} from 'common/util/react2panel';

import {ModalReact} from './component';
import {ModalAttrs, ModalChangeEvent} from './types';

interface ModalJsxProps {
  attrs?: ModalAttrs;
  on?: {
    change?: (ev: ModalChangeEvent) => void;
    'stackable-closed'?: (ev: CustomEvent<{target: string; stackSize: number}>) => void;
    'stackable-open'?: (ev: CustomEvent<{target: string; stackSize: number}>) => void;
  };
}

// Attrs the FC takes as camelCase props, and whether Panel read each one by presence: snabbdom
// wrote any value but `false`/`undefined` as a present attribute, so a call site passing `''` meant
// `true`. Unlisted keys (aria-*, data-*, elref, role) keep their name and land on the host.
const PROP_ATTRS: Record<string, `boolean` | `value`> = {
  closeable: `boolean`,
  'close-button-padded': `boolean`,
  'close-confirm-text': `value`,
  'close-of-same-type': `boolean`,
  'grow-to-content': `boolean`,
  'hide-backdrop': `boolean`,
  'hide-background': `boolean`,
  'hide-close-button': `boolean`,
  'ignore-stacking': `boolean`,
  'modal-description': `value`,
  'modal-title': `value`,
  'modal-type': `value`,
  'not-fullscreen': `boolean`,
  open: `boolean`,
  'sliding-transition': `boolean`,
  'style-override': `value`,
  'vertical-alignment': `value`,
};

const ON_TO_PROP: Record<string, string> = {
  change: `onChange`,
  'stackable-open': `onStackableOpen`,
  'stackable-closed': `onStackableClosed`,
};

const toCamelCase = (attr: string) => attr.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());

// Lazy init to avoid a load-time circular dependency with `panel`. useShadowDom lets the FC's
// <slot> project the children this shim forwards onto the host.
let cachedInner: ((props: Record<string, unknown>, children?: JsxVNodeChildren) => VNode) | null = null;

const getInner = () => {
  if (!cachedInner) {
    cachedInner = react2panel(`mp-modal`, ModalReact, {useShadowDom: true}) as unknown as (
      props: Record<string, unknown>,
      children?: JsxVNodeChildren,
    ) => VNode;
  }
  return cachedInner;
};

// Panel-facing shim: accepts the `<ModalEl attrs={{...}} on={{...}}>` call shape and forwards flat
// React props to the react2panel-wrapped FC.
export const ModalEl = (panelProps?: StrictJsxProps<ModalJsxProps>, children?: JsxVNodeChildren): VNode => {
  const {sel = ``, hook, class: klass, style, attrs = {}, on = {}} = panelProps ?? {};

  const props: Record<string, unknown> = {};
  for (const [attr, value] of Object.entries(attrs)) {
    const kind = PROP_ATTRS[attr];
    props[kind ? toCamelCase(attr) : attr] = kind === `boolean` ? value !== false && value !== undefined : value;
  }
  for (const [event, handler] of Object.entries(on)) {
    if (ON_TO_PROP[event]) {
      props[ON_TO_PROP[event]] = handler;
    }
  }
  // react2panel names the wrapper `<mp-modal-{hash}>`, so consumer stylesheets written against the
  // old `mp-modal` tag selector need the legacy name as a class instead.
  props.sel = `${sel}.mp-modal`;
  if (hook) {
    props.hook = hook;
  }
  if (style) {
    props.style = style;
  }
  const className = clsx(klass);
  if (className) {
    props.className = className;
  }

  return getInner()(props, children);
};
